/**
 * Display-name helpers used across every screen.
 *
 * Rule: nowhere in the UI do we show a raw enum / code / UUID. The user
 * always sees a humanised label. This module owns the translation so the
 * mappings stay consistent — change a label here, every screen updates.
 *
 * - `humanRole`         site_admin                  -> "Site Admin"
 * - `humanStatus`       active / invited / locked   -> "Active" / "Invited"
 * - `humanAction`       auth.login.complete         -> "Logged in"
 * - `humanResourceType` user / invitation / study   -> "User" / "Invitation"
 * - `humanAmr`          ['pwd','totp']              -> "Password + Authenticator"
 * - `titleCase`         snake_case_value            -> "Snake Case Value" (fallback)
 */

const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
  application_admin: 'Application Admin',
  compliance: 'Compliance',
  study_designer: 'Study Designer',
  site_admin: 'Site Admin',
  principal_investigator: 'Principal Investigator',
  sub_investigator: 'Sub-Investigator',
  // Display names only (client, 16 Oct 2026); the stored codes are unchanged.
  coordinator: 'Study Coordinator',
  site_study_designer: 'Site Study Designer',
  regulatory_specialist: 'Regulatory Specialist',
  site_pharma: 'Site Pharmacist',
  finance_billing: 'Finance / Billing',
  monitor: 'Monitor',
  auditor: 'Auditor',
  clinical_nurse: 'Nurse Practitioner',
  clinical_lab_assistant: 'Clinical Lab Assistant',
  clinical_qe: 'Clinical QE',
  external_lab: 'Others', // display only (client, 16 Oct 2026); code unchanged
  // F6 - base site membership. Displayed as "Site User" everywhere the
  // audit / people surfaces render a role label.
  site_user: 'Site User',
  invitee: 'Invitee',
  // Sponsor / regulatory personas. Reserved names in the Role union that no
  // guard enforces yet, but they are selectable on admin surfaces (eISF
  // approver lists, RBAC configuration), where titleCase() rendered
  // "Irb Administrator". Display-only — adding a label changes no behaviour.
  irb_administrator: 'IRB Administrator',
  sponsor_regulatory_affairs: 'Sponsor Regulatory Affairs',
  regulatory_inspector: 'Regulatory Inspector',
  data_manager: 'Data Manager',
  sponsor_project_manager: 'Sponsor Project Manager',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  invited: 'Invited',
  locked: 'Locked',
  disabled: 'Disabled',
  inactive: 'Inactive',
  onboarding: 'Onboarding',
  pending: 'Pending',
  enrolled: 'Enrolled',
  consented: 'Consented',
  screening: 'Screening',
  withdrawn: 'Withdrawn',
  completed: 'Completed',
  planning: 'Planning',
  paused: 'Paused',
  closed: 'Closed',
  queued: 'Queued',
  running: 'Running',
  ready: 'Ready',
  failed: 'Failed',
  // Study lifecycle (Sprint-2 DREAM-17 / DREAM-18 7-stage)
  draft: 'Draft',
  initiation: 'Initiation',
  closed_to_enrollment: 'Closed to Enrollment',
  follow_up: 'Follow-up',
  archived: 'Archived',
};

/** Tailwind-ish color classes for status badges across the 7-stage study lifecycle. */
export const STUDY_STATUS_BADGE_CLASS: Record<string, string> = {
  draft:                'badge badge-neutral',
  initiation:           'badge badge-warning',
  active:               'badge badge-success',
  closed_to_enrollment: 'badge badge-danger',
  follow_up:            'badge badge-purple',
  completed:            'badge badge-teal',
  archived:             'badge badge-slate',
  // legacy
  planning:             'badge badge-neutral',
  paused:               'badge badge-warning',
  closed:               'badge badge-slate',
};

/** Readiness flag labels for DREAM-18 four-component checklist. */
const READINESS_LABELS: Record<string, string> = {
  econsent: 'eConsent',
  ebinder: 'eBinder / eISF',
  staff: 'Staff',
  esource: 'eSource',
};

/** Onboarding step labels (DREAM-18 stepper). */
const ONBOARDING_STEP_LABELS: Record<string, string> = {
  '1': 'Study Invitation',
  '2': 'Protocol Setup',
  '3': 'Study Customisation',
  '4': 'eSignature Approval',
  '5': 'Activation',
  study_invitation: 'Study Invitation',
  protocol_setup: 'Protocol Setup',
  study_customisation: 'Study Customisation',
  esignature_approval: 'eSignature Approval',
  activation: 'Activation',
};

/** Document processing state (DREAM-19 AC16). */
const PROCESSING_STATE_LABELS: Record<string, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  needs_attention: 'Needs attention',
};

/** Study link categories (DREAM-17 Study Links panel). */
const LINK_CATEGORY_LABELS: Record<string, string> = {
  lms: 'LMS / Training',
  randomization: 'Randomization',
  regulatory: 'Regulatory / Compliance',
  other: 'Other',
};

/**
 * eConsent method labels — displayed on the Consent Method picker and any
 * post-signed read-back. Keys match the BE enum on `ctms_consent_records`
 * transient metadata (`consent_mode`): `in_person | in_person_esign | remote`.
 * `paper_sign` and `esign` are additional keys used by read-back paths that
 * can only distinguish paper vs electronic (the `docuseal_submission_id`
 * sentinel `'in_person'` marks paper).
 */
const CONSENT_MODE_LABELS: Record<string, string> = {
  in_person: 'Paper Sign',
  in_person_esign: 'In-Person e-Sign',
  remote: 'Remote e-Sign',
  paper_sign: 'Paper Sign',
  esign: 'e-Sign',
};

const ACTION_LABELS: Record<string, string> = {
  // Auth
  'auth.login.password.success': 'Password verified',
  'auth.login.password.failure': 'Password failed',
  'auth.login.complete': 'Logged in',
  'auth.logout': 'Logged out',
  'auth.token.refreshed': 'Session refreshed',
  'auth.site_switched': 'Switched site',
  'auth.mfa.enrolled': 'MFA enrolled',
  'auth.mfa.bypassed': 'MFA bypassed (testing)',
  'auth.mfa.recovery_codes.generated': 'Recovery codes generated',
  'auth.password_reset.requested': 'Password reset requested',
  'auth.password_reset.completed': 'Password reset completed',
  'auth.password.changed': 'Password changed',
  'auth.password.locked': 'Account locked (failed attempts)',
  'auth.unlocked': 'Account unlocked',
  'auth.password_reset.locked': 'Reset locked (failed attempts)',
  // Sites
  'site.created': 'Site created',
  'site.updated': 'Site updated',
  'site.disabled': 'Site disabled',
  'site.enabled': 'Site enabled',
  'site.deleted': 'Site deleted',
  'site.onboard_failed': 'Site onboarding failed',
  // Invites
  'invite.sent': 'Invitation sent',
  'invite.resent': 'Invitation resent',
  'invite.activated': 'Invitation accepted',
  'invite.expired': 'Invitation expired',
  // Admin
  'admin.user.created': 'User created',
  'admin.user.deleted': 'User deleted',
  'admin.user.locked': 'User locked',
  'admin.user.unlocked': 'User unlocked',
  'admin.user.role_changed': 'Role changed',
  'admin.seed.test_study': 'Test study seeded',
  // Subjects
  'subject.created': 'Subject created',
  'subject.updated': 'Subject updated',
  // PHI
  'phi.grant.created': 'PHI access granted',
  'phi.grant.revoked': 'PHI access revoked',
  'phi.remote_view.enabled': 'Remote view enabled',
  'phi.remote_view.disabled': 'Remote view disabled',
  // CSR
  'csr.job.created': 'CSR export requested',
  'csr.job.started': 'CSR export started',
  'csr.job.ready': 'CSR export ready',
  'csr.job.failed': 'CSR export failed',
  'csr.job.downloaded': 'CSR export downloaded',
  'csr.job.expired': 'CSR export expired',
  // Compliance
  'compliance.audit_trail.exported': 'Audit trail exported',
  'compliance.access_log.exported': 'Access log exported',
  'compliance.csr_prep.reauth_failed': 'Reauth failed (CSR)',
  // Studies (Sprint-2 — DREAM-16 CTMS core)
  'study.created': 'Study created',
  'study.updated': 'Study updated',
  'study.activated': 'Study activated',
  'study.enrollment_closed': 'Enrollment closed',
  'study.moved_to_follow_up': 'Moved to follow-up',
  'study.completed': 'Study completed',
  'study.archived': 'Study archived',
  'study.team_changed': 'Study team changed',
  'study.onboarding.step_completed': 'Onboarding step completed',
  'study.onboarding.resumed': 'Onboarding resumed',
  'study.readiness.changed': 'Readiness flag changed',
  'study.link.created': 'Study link added',
  'study.link.deleted': 'Study link removed',
  'study.document.uploaded': 'Protocol document uploaded',
  'study.document.replaced': 'Protocol document replaced',
  'study.document.failed': 'Protocol upload failed',
  'study.document.retry': 'Protocol upload retried',
  'study.visit.unscheduled_created': 'Unscheduled visit added',
  'study.visit.data_entered': 'Visit data entered',
  'study.staff.assigned': 'Staff member assigned',
  'study.staff.revoked': 'Staff member removed',
  'study.locked': 'Study locked',
  'study.unlocked': 'Study unlocked',
  'study.status.transitioned': 'Study status changed',
};

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  user: 'User',
  invitation: 'Invitation',
  site: 'Site',
  study: 'Study',
  study_site: 'Study at site',
  subject: 'Subject',
  visit: 'Visit',
  csr_export: 'CSR Export',
  phi_grant: 'PHI Grant',
  query: 'Query',
  // Sprint-2 — DREAM-16 CTMS core
  study_link: 'Study link',
  study_document: 'Study document',
  onboarding_step: 'Onboarding step',
  study_form: 'Form',
  visit_template: 'Visit template',
};

/**
 * Failure / state-transition reason codes the backend emits in `new_value.reason`.
 * Anything not listed here goes through `titleCase` so we never leak snake_case.
 */
const REASON_LABELS: Record<string, string> = {
  user_locked: 'Account locked',
  user_disabled: 'Account disabled',
  user_inactive: 'Account inactive',
  invalid_password: 'Invalid password',
  invalid_recovery: 'Invalid recovery code',
  invalid_token: 'Invalid token',
  expired_token: 'Expired token',
  mfa_required: 'MFA required',
  mfa_invalid: 'Invalid MFA code',
  mfa_window_expired: 'MFA window expired',
  recovery_codes_exhausted: 'Recovery codes exhausted',
  too_many_attempts: 'Too many attempts',
  not_found: 'Not found',
  permission_denied: 'Permission denied',
  session_expired: 'Session expired',
  rate_limited: 'Rate limited',
  email_not_verified: 'Email not verified',
  user_not_enrolled: 'User not enrolled in MFA',
  bypass_enabled: 'Test bypass enabled',
  idle: 'Idle timeout',
  manual: 'Manual sign-out',
};

export function humanReason(reason: string | null | undefined): string {
  if (!reason) return '';
  return REASON_LABELS[reason] ?? titleCase(reason);
}

/** DREAM-18 readiness flag label (eConsent / eBinder / Staff / eSource). */
export function humanReadiness(flag: string | null | undefined): string {
  if (!flag) return '';
  return READINESS_LABELS[flag] ?? titleCase(flag);
}

/**
 * Format an ISO-ish date string ("YYYY-MM-DD" or full ISO) as a
 * locale-friendly day label, e.g. "15 March 2026". Returns the raw input
 * if it can't be parsed so we never blank out a value the BE sent.
 */
function formatHumanDate(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value);
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  try {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return s;
  }
}

/** Row in the humanized "View evidence" disclosure for a readiness flag. */
export interface ReadinessEvidenceRow {
  label: string;
  value: string;
  /** When true the row should be rendered in a danger color (used for BE errors). */
  isError?: boolean;
}

/**
 * DREAM-18 — Humanize a derived-readiness-flag's `evidence` blob into a
 * list of {label, value} rows for the "View evidence" disclosure. Each
 * flag has its own contract (derived from the BE `derivedReadiness.ts`):
 *
 *  - econsent: active_consent_document / consent_document_id / version_label /
 *              docuseal_template_id / irb_name / irb_approval_date
 *  - ebinder:  total_protocol_documents / completed_count / latest_completed_version_label
 *  - staff:    total_delegations / active_count
 *  - esource:  visit_template_count
 *
 * Any flag may also carry an `error` key — surfaced as a danger-colored row.
 *
 * Rows whose value is null/undefined are omitted, except where absence is
 * itself meaningful (e.g. "Active ICF version: none" when the BE explicitly
 * sets `active_consent_document: null`).
 */
export function humanizeReadinessEvidence(
  flagKey: string,
  evidence: Record<string, unknown> | null | undefined,
): ReadinessEvidenceRow[] {
  if (!evidence || typeof evidence !== 'object') return [];
  const rows: ReadinessEvidenceRow[] = [];

  // Universal: BE error surfaces in any flag's evidence.
  if (typeof evidence.error === 'string' && evidence.error.length > 0) {
    rows.push({ label: 'Error', value: evidence.error, isError: true });
  }

  if (flagKey === 'econsent') {
    // Explicit "no active consent" signal — value is intentionally meaningful.
    if ('active_consent_document' in evidence && evidence.active_consent_document === null) {
      rows.push({ label: 'Active ICF version', value: 'none' });
    }
    if (typeof evidence.version_label === 'string' && evidence.version_label) {
      rows.push({ label: 'Active ICF version', value: evidence.version_label });
    }
    if (typeof evidence.consent_document_id === 'string' && evidence.consent_document_id) {
      rows.push({ label: 'Document ID', value: `${evidence.consent_document_id.slice(0, 8)}…` });
    }
    if ('docuseal_template_id' in evidence) {
      const v = evidence.docuseal_template_id;
      if (v === null || v === undefined || v === '') {
        rows.push({ label: 'DocuSeal template ID', value: 'not set' });
      } else {
        rows.push({ label: 'DocuSeal template ID', value: String(v) });
      }
    }
    if (typeof evidence.irb_name === 'string' && evidence.irb_name) {
      rows.push({ label: 'IRB', value: evidence.irb_name });
    }
    if (evidence.irb_approval_date !== undefined && evidence.irb_approval_date !== null) {
      const formatted = formatHumanDate(evidence.irb_approval_date);
      if (formatted) rows.push({ label: 'IRB approval date', value: formatted });
    }
    return rows;
  }

  if (flagKey === 'ebinder') {
    if (typeof evidence.total_protocol_documents === 'number') {
      rows.push({ label: 'Protocol documents on file', value: String(evidence.total_protocol_documents) });
    }
    if (typeof evidence.completed_count === 'number') {
      rows.push({ label: 'Completed', value: String(evidence.completed_count) });
    }
    if (typeof evidence.latest_completed_version_label === 'string' && evidence.latest_completed_version_label) {
      rows.push({ label: 'Latest version', value: evidence.latest_completed_version_label });
    }
    return rows;
  }

  if (flagKey === 'staff') {
    if (typeof evidence.total_delegations === 'number') {
      rows.push({ label: 'Total delegations', value: String(evidence.total_delegations) });
    }
    if (typeof evidence.active_count === 'number') {
      rows.push({ label: 'Active (in-window, non-revoked)', value: String(evidence.active_count) });
    }
    return rows;
  }

  if (flagKey === 'esource') {
    if (typeof evidence.visit_template_count === 'number') {
      rows.push({ label: 'Visit templates defined', value: String(evidence.visit_template_count) });
    }
    return rows;
  }

  // Unknown flag — title-case every key as a defensive fallback so we never
  // leak raw JSON to the user.
  for (const [k, v] of Object.entries(evidence)) {
    if (k === 'error') continue; // already handled above
    if (v === null || v === undefined) continue;
    rows.push({ label: titleCase(k), value: String(v) });
  }
  return rows;
}

/** DREAM-18 onboarding step label by step number or step key. */
export function humanOnboardingStep(step: string | number | null | undefined): string {
  if (step === null || step === undefined) return '';
  const key = String(step);
  return ONBOARDING_STEP_LABELS[key] ?? titleCase(key);
}

/** DREAM-19 document processing state label. */
export function humanProcessingState(state: string | null | undefined): string {
  if (!state) return '';
  return PROCESSING_STATE_LABELS[state] ?? titleCase(state);
}

/** DREAM-17 study link category label. */
export function humanLinkCategory(category: string | null | undefined): string {
  if (!category) return '';
  return LINK_CATEGORY_LABELS[category] ?? titleCase(category);
}

/** eConsent method label — 'in_person' / 'in_person_esign' / 'remote'. */
export function humanConsentMode(mode: string | null | undefined): string {
  if (!mode) return '';
  return CONSENT_MODE_LABELS[mode] ?? titleCase(mode);
}

/** DREAM-17 / DREAM-18 study lifecycle stage label (handles legacy values). */
export function humanStudyStatus(status: string | null | undefined): string {
  return humanStatus(status);
}

/** Tailwind badge class for a study status badge. Handles legacy values. */
export function studyStatusBadgeClass(status: string | null | undefined): string {
  if (!status) return 'badge badge-neutral';
  return STUDY_STATUS_BADGE_CLASS[status] ?? 'badge badge-neutral';
}

const AMR_LABELS: Record<string, string> = {
  pwd: 'Password',
  totp: 'Authenticator',
  email: 'Email OTP',
  recovery: 'Recovery Code',
};

export function titleCase(snake: string | null | undefined): string {
  if (!snake) return '';
  return snake
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function humanRole(role: string | null | undefined): string {
  if (!role) return '—';
  return ROLE_LABELS[role] ?? titleCase(role);
}

export function humanStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_LABELS[status] ?? titleCase(status);
}

export function humanAction(action: string | null | undefined): string {
  if (!action) return '—';
  return ACTION_LABELS[action] ?? titleCase(action.replace(/[._]/g, ' '));
}

/**
 * Context-aware action label that incorporates key payload fields.
 * For onboarding step events, returns the specific step name instead of the
 * generic "Onboarding step completed" — required for legible 21 CFR Part 11 audit trails.
 */
export function humanActionLabel(
  action: string | null | undefined,
  newValue: string | Record<string, unknown> | null | undefined,
): string {
  if (!action) return '—';
  if (
    action === 'study.onboarding.step_completed' ||
    action === 'study.onboarding.draft_saved'
  ) {
    const v: Record<string, unknown> =
      typeof newValue === 'string'
        ? (() => { try { return JSON.parse(newValue) as Record<string, unknown>; } catch { return {}; } })()
        : (newValue ?? {});
    const step = v.step;
    if (step != null) {
      const stepName = ONBOARDING_STEP_LABELS[String(step)];
      if (stepName) {
        return action === 'study.onboarding.step_completed'
          ? `${stepName} — completed`
          : `${stepName} — draft saved`;
      }
    }
  }
  return ACTION_LABELS[action] ?? titleCase(action.replace(/[._]/g, ' '));
}

export function humanResourceType(type: string | null | undefined): string {
  if (!type) return '';
  return RESOURCE_TYPE_LABELS[type] ?? titleCase(type);
}

export function humanAmr(amr: string[] | string | null | undefined): string {
  if (!amr) return '';
  const list = Array.isArray(amr) ? amr : [amr];
  return list.map((m) => AMR_LABELS[m] ?? titleCase(m)).join(' + ');
}

/**
 * Render an audit row's resource (e.g., "User: Jane Smith" or
 * "Invitation: alice@…"). Caller supplies a lookup table from id to a
 * human name. Falls back to "Type: short-id" when the id isn't in the map.
 */
export function humanResource(
  resourceType: string | null | undefined,
  resourceId: string | null | undefined,
  lookups: {
    users?: Map<string, { full_name: string; email: string }>;
    sites?: Map<string, string>;
    studies?: Map<string, string>;
  } = {},
): string {
  if (!resourceType || !resourceId) return '—';
  const typeLabel = humanResourceType(resourceType);
  const shortId = resourceId.slice(0, 8);

  if (resourceType === 'user') {
    const u = lookups.users?.get(resourceId);
    if (u) return `${typeLabel}: ${u.full_name || u.email}`;
  }
  if (resourceType === 'site') {
    const name = lookups.sites?.get(resourceId);
    if (name) return `${typeLabel}: ${name}`;
  }
  if (resourceType === 'study') {
    const name = lookups.studies?.get(resourceId);
    if (name) return `${typeLabel}: ${name}`;
  }

  return `${typeLabel}: ${shortId}`;
}

/**
 * Humanise the audit row's `new_value` / `old_value` payload. Tries to
 * format the well-known keys; falls back to a compact key/value list (no
 * raw JSON braces). Returns null when there's nothing meaningful to show.
 */
export function humanDetails(
  action: string | null | undefined,
  newValue: string | Record<string, unknown> | null,
  lookups: {
    users?: Map<string, { full_name: string; email: string }>;
    sites?: Map<string, string>;
    studies?: Map<string, string>;
  } = {},
): string | null {
  if (!newValue) return null;
  let v: Record<string, unknown>;
  if (typeof newValue === 'string') {
    try {
      v = JSON.parse(newValue) as Record<string, unknown>;
    } catch {
      return newValue; // not JSON — show as-is
    }
  } else {
    v = newValue;
  }
  if (!v || typeof v !== 'object') return null;

  const parts: string[] = [];

  // Auth-related
  if (v.amr) parts.push(`Method: ${humanAmr(v.amr as string[])}`);
  if (v.reason && typeof v.reason === 'string') parts.push(`Reason: ${humanReason(v.reason)}`);
  if (v.attempts !== undefined) parts.push(`Attempts: ${v.attempts}`);
  if (v.locked === true) parts.push('Account was locked');

  // Invite-related
  if (v.email && typeof v.email === 'string') parts.push(`Email: ${v.email}`);
  if (v.target_email && typeof v.target_email === 'string') parts.push(`Target: ${v.target_email}`);
  if (v.invite_source) parts.push(`Invited by: ${humanRole(String(v.invite_source))}`);
  if (v.granted_role) parts.push(`Role granted: ${humanRole(String(v.granted_role))}`);
  if (v.role && !v.granted_role) parts.push(`Role: ${humanRole(String(v.role))}`);

  // Staff assignment / revocation (study.staff.*)
  if (v.target_user_name && typeof v.target_user_name === 'string')
    parts.push(`Member: ${v.target_user_name}`);
  if (v.actor_email && typeof v.actor_email === 'string')
    parts.push(`By: ${v.actor_email}`);

  // Onboarding wizard step — step is stored as a number by the backend
  if (v.step != null)
    parts.push(`Step: ${ONBOARDING_STEP_LABELS[String(v.step)] ?? titleCase(String(v.step))}`);

  // Readiness flag change (study.readiness.changed)
  if (v.flag && typeof v.flag === 'string') {
    const flagLabel = READINESS_LABELS[v.flag] ?? titleCase(v.flag);
    const readyStr = v.value === true ? 'Ready' : v.value === false ? 'Not ready' : String(v.value);
    parts.push(`${flagLabel}: ${readyStr}`);
  }

  // Protocol document upload (study.document.uploaded / replaced)
  if (v.file_name && typeof v.file_name === 'string')
    parts.push(`File: ${v.file_name}`);
  if (v.version_label && typeof v.version_label === 'string')
    parts.push(`Version: ${v.version_label}`);

  // Site / study
  if (
    v.name &&
    typeof v.name === 'string' &&
    (action?.startsWith('site.') || action?.startsWith('study.'))
  ) {
    parts.push(`Name: ${v.name}`);
  }
  if (v.protocol_number) parts.push(`Protocol: ${v.protocol_number}`);
  if (v.sponsor) parts.push(`Sponsor: ${v.sponsor}`);
  if (v.region) parts.push(`Region: ${v.region}`);
  if (v.s3_bucket_name && typeof v.s3_bucket_name === 'string')
    parts.push(`Bucket: ${v.s3_bucket_name.slice(0, 32)}…`);

  // CSR
  if (v.pii_included !== undefined) parts.push(`PHI included: ${v.pii_included ? 'yes' : 'no'}`);
  if (v.requested_by_role) parts.push(`Requested by: ${humanRole(String(v.requested_by_role))}`);
  if (v.size_bytes !== undefined && typeof v.size_bytes === 'number') {
    parts.push(`Size: ${(v.size_bytes / 1024).toFixed(1)} KB`);
  }

  // Admin user changes
  if (v.from_site_id || v.to_site_id) {
    const from = v.from_site_id
      ? (lookups.sites?.get(String(v.from_site_id)) ?? 'Unknown site')
      : '—';
    const to = v.to_site_id ? (lookups.sites?.get(String(v.to_site_id)) ?? 'Unknown site') : '—';
    parts.push(`From: ${from} → To: ${to}`);
  }

  // Error
  if (v.error && typeof v.error === 'string') parts.push(`Error: ${v.error.slice(0, 120)}`);

  return parts.length > 0 ? parts.join(' · ') : null;
}
